import Promise from 'bluebird';
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import {
  makeUseOptionsAsyncDataSourceWithClientFilter,
  makeUseSelectedOptionsAsyncDataSource,
} from '@bingads-webui-campaign-react/multi-selector';
import { loadBrandListsFactory, loadCampaignAssociatedBrandListsFactory } from '@bingads-webui-campaign-react/brand-lists-data';
import { BrandListsPickerContent } from './brand-lists-picker-content';
import { BrandListsContextProvider } from './brand-lists-data';
import { BRAND_LIST_MAX_COUNT, BRAND_LIST_OPTIONS_DISPLAY_LIMIT, BRAND_LIST_SELECTED_OPTIONS_LIMIT } from '../constants';

const filterPredicate = searchFilter => (_.isEmpty(searchFilter) ?
  _.constant(true) :
  option => option.Name.toLowerCase().includes(searchFilter.toLowerCase()));

export const BrandListsPicker = ({
  className,
  disabled,
  selectedOptionsLimit,
  optionsDisplayLimit,
  loadBrandListsFactory: loadBrandListsFactoryFromProp,
  loadCampaignAssociatedBrandListsFactory: loadCampaignAssociatedBrandListsFactoryFromProp,
  isConfirmDialogOn,
  cid,
  aid,
  campaignId,
  onChange,
  onSelectedOptionsUpdate,
  savedAddedSelectedOptionsKeys,
  savedDeletedSelectedOptionsKeys,
  descriptionTextTemplate,
  noInitialDataText,
}) => {
  const loadBrandLists = useMemo(() => loadBrandListsFactoryFromProp(cid, aid), [loadBrandListsFactoryFromProp, cid, aid]);
  const loadSelectedBrandLists = useMemo(
    () => (campaignId ? loadCampaignAssociatedBrandListsFactoryFromProp(cid, aid, campaignId) : () => Promise.delay(0, [])),
    [loadCampaignAssociatedBrandListsFactoryFromProp, cid, aid, campaignId]
  );

  const [isBrandListLimitReached, setIsBrandListLimitReached] = useState(false);
  const onDoneCallback = useCallback(allOptions => setIsBrandListLimitReached(allOptions && allOptions.length >= BRAND_LIST_MAX_COUNT), []);

  const useOptions = useMemo(() => makeUseOptionsAsyncDataSourceWithClientFilter(loadBrandLists, filterPredicate, onDoneCallback), [loadBrandLists, onDoneCallback]);
  const useSelectedOptions = useMemo(() => makeUseSelectedOptionsAsyncDataSource(loadSelectedBrandLists), [loadSelectedBrandLists]);

  return (
    <BrandListsContextProvider
      useOptions={useOptions}
      useSelectedOptions={useSelectedOptions}
      selectedOptionsLimit={selectedOptionsLimit}
      optionsDisplayLimit={optionsDisplayLimit}
      onSelectedOptionsUpdate={onSelectedOptionsUpdate}
      onChange={onChange}
      savedAddedSelectedOptionsKeys={savedAddedSelectedOptionsKeys}
      savedDeletedSelectedOptionsKeys={savedDeletedSelectedOptionsKeys}
    >
      <BrandListsPickerContent
        cid={cid}
        aid={aid}
        className={className}
        disabled={disabled}
        isConfirmDialogOn={isConfirmDialogOn}
        isBrandListLimitReached={isBrandListLimitReached}
        descriptionTextTemplate={descriptionTextTemplate}
        noInitialDataText={noInitialDataText}
      />
    </BrandListsContextProvider>
  );
};

BrandListsPicker.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  selectedOptionsLimit: PropTypes.number,
  optionsDisplayLimit: PropTypes.number,
  loadBrandListsFactory: PropTypes.func,
  loadCampaignAssociatedBrandListsFactory: PropTypes.func,
  isConfirmDialogOn: PropTypes.bool,
  cid: PropTypes.number.isRequired,
  aid: PropTypes.number.isRequired,
  campaignId: PropTypes.number,
  onChange: PropTypes.func,
  onSelectedOptionsUpdate: PropTypes.func,
  savedAddedSelectedOptionsKeys: PropTypes.arrayOf(PropTypes.number),
  savedDeletedSelectedOptionsKeys: PropTypes.arrayOf(PropTypes.number),
  descriptionTextTemplate: PropTypes.string,
  noInitialDataText: PropTypes.string,
};

BrandListsPicker.defaultProps = {
  className: undefined,
  disabled: false,
  selectedOptionsLimit: BRAND_LIST_SELECTED_OPTIONS_LIMIT,
  optionsDisplayLimit: BRAND_LIST_OPTIONS_DISPLAY_LIMIT,
  loadBrandListsFactory,
  loadCampaignAssociatedBrandListsFactory,
  isConfirmDialogOn: false,
  campaignId: undefined,
  onChange: undefined,
  onSelectedOptionsUpdate: undefined,
  savedAddedSelectedOptionsKeys: [],
  savedDeletedSelectedOptionsKeys: [],
  descriptionTextTemplate: '',
  noInitialDataText: undefined,
};
